import DOMPurify from 'dompurify'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Calculate, Comment, Label, PriorityHigh } from '@mui/icons-material'
import { Box, Button, Divider, Grid, MenuItem, Skeleton, Stack, TextField, Typography } from '@mui/material'

import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import VersionTagsList from '../../../../../components/VersionTagsList/VersionTagsList'
import languageService from '../../../../../services/LanguageService'
import { getReleaseNotes } from '../../../games/helpers/versionTagsHelpers'
import { GameVersion } from '../../../games/types/GameAnalyses'
import { InternalAnalysisComment } from '../../../review/types/InternalReview'
import { findVersion, formatReleaseDate, getVersionNames, isPreviouslyFinalized } from '../../helpers/analysisOverviewHelpers'
import { InternalAnalysis } from '../../types/InternalAnalysis'

type AnalysisVersionSectionProps = {
  analysis: InternalAnalysis
  // Newest first.
  versions: GameVersion[]
  isLoadingVersions?: boolean
  versionsFailed?: boolean
  canCalculate: boolean
  disabled?: boolean
  comment?: InternalAnalysisComment | null
  commentEditUrl: string
  onVersionChange: (version: string) => void
  onCalculate: () => void
  onEditVersionTags: () => void
}

export const AnalysisVersionSection: FC<AnalysisVersionSectionProps> = ({
  analysis,
  versions,
  isLoadingVersions,
  versionsFailed,
  canCalculate,
  disabled,
  comment,
  commentEditUrl,
  onVersionChange,
  onCalculate,
  onEditVersionTags,
}) => {
  const { t } = useTranslation()
  const versionNames = useMemo(() => getVersionNames(versions), [versions])
  const versionTags = useMemo(() => languageService.getVersionTags(), [])
  const selectedVersion = findVersion(versions, analysis.gameVersion)
  const selectedTags = versionTags.filter(({ id }) => selectedVersion?.tags?.includes(id))
  const storedVersion = analysis.gameVersion ?? ''
  const commentHtml = comment?.content?.comment?.en
  const finalized = isPreviouslyFinalized(analysis)

  const renderVersionSelect = () => {
    if (isLoadingVersions) return <Skeleton variant="rectangular" height={40} />
    if (versionsFailed) return <Typography color="error.main">{t('internal-analysts:analysis_overview_versions_failed')}</Typography>
    if (!versionNames.length) return null
    return (
      <TextField
        select
        size="small"
        fullWidth
        label={t('internal-analysts:analysis_overview_version')}
        value={storedVersion}
        disabled={disabled}
        onChange={(event) => event.target.value && event.target.value !== storedVersion && onVersionChange(event.target.value)}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
      >
        <MenuItem value="" disabled>
          {t('internal-analysts:analysis_overview_version_none')}
        </MenuItem>
        {versionNames.map((name) => (
          <MenuItem key={name} value={name}>
            {name}
          </MenuItem>
        ))}
        {storedVersion && !versionNames.includes(storedVersion) && <MenuItem value={storedVersion}>{storedVersion}</MenuItem>}
      </TextField>
    )
  }

  // Grid's negative spacing margins are reset when it is a direct child of a Stack, so it needs its own wrapper.
  return (
    <Box>
      <Grid container wrap="wrap" spacing={4}>
        <Grid item xs={12} md={5}>
          <Stack spacing={2} alignItems="flex-start">
            <Box width="100%">{renderVersionSelect()}</Box>
            <Button variant="contained" color="success" startIcon={<Calculate />} disabled={disabled || !canCalculate} onClick={onCalculate}>
              {t(finalized ? 'internal-analysts:analysis_overview_recalculate' : 'internal-analysts:analysis_overview_calculate')}
            </Button>
            {!canCalculate && !disabled && (
              <Typography variant="body2" color="text.secondary">
                {t('internal-analysts:analysis_overview_calculate_help')}
              </Typography>
            )}
          </Stack>
        </Grid>
        {selectedVersion && (
          <Grid item xs={12} md={7}>
            <Stack spacing={1}>
              <Typography variant="h6">
                {t('internal-analysts:analysis_overview_version_release', {
                  version: selectedVersion.version,
                  date: formatReleaseDate(selectedVersion.versionReleaseDate),
                })}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {t('internal-analysts:analysis_overview_release_notes')}
              </Typography>
              <Typography variant="body2" whiteSpace="pre-line">
                {getReleaseNotes(selectedVersion) || '-'}
              </Typography>
              {selectedTags.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  <VersionTagsList versionTags={selectedTags} selectedVersionTags={selectedTags} />
                </Box>
              )}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button variant="outlined" size="small" startIcon={<Label />} onClick={onEditVersionTags}>
                  {t('internal-analysts:analysis_overview_edit_version_tags')}
                </Button>
                <Button variant="outlined" size="small" startIcon={<Comment />} href={commentEditUrl} target="_blank" rel="noopener noreferrer">
                  {t('internal-analysts:analysis_overview_edit_comment')}
                </Button>
                {commentHtml && (
                  <GRTooltip content={<Box dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(commentHtml) }} />}>
                    <PriorityHigh color="success" aria-label={t('internal-analysts:analysis_overview_has_comment')} />
                  </GRTooltip>
                )}
              </Stack>
              <Divider />
              <Typography variant="body2" fontWeight="bold">
                {t('internal-analysts:analysis_overview_all_release_notes')}
              </Typography>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }} data-testid="all-release-notes">
                <Stack spacing={2}>
                  {versions.map((version) => (
                    <Box key={version.id ?? version.version}>
                      <Typography variant="body2" fontWeight="bold">
                        {version.version} / {formatReleaseDate(version.versionReleaseDate)}
                      </Typography>
                      <Typography variant="body2" whiteSpace="pre-line">
                        {version.releaseNotes}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}
